/**
 * CookieConsentBanner - GDPR cookie consent UI
 *
 * v1.4 - GDPR-03: Link to /legal/cookies (Cookie Policy) instead of /legal/privacy - 2026-03-17
 * v1.3 - GDPR-03: Suppress banner on /legal/* pages; Manage preferences panel; update privacy link to /legal/privacy - 2026-03-17
 * v1.2 - Privacy Policy opens in new tab (user stays on cookie decision) - 2026-03-17
 * v1.1 - Fixed Privacy Policy link to use SPA navigation (no full reload) - 2026-03-17
 * v1.0 - GDPR-02: Cookie consent banner - 2026-03-15
 * - Fixed bottom bar, does not block content
 * - "Accept All" and "Essential Only" buttons
 * - Slide-up animation, dismisses smoothly
 * - Mobile responsive
 * - No third-party CMP library
 */
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Link,
  Typography,
  Slide,
  Switch,
  Collapse,
  Divider,
} from '@mui/material';
import { useCookieConsent } from '../hooks/useCookieConsent';

const CookieConsentBanner: React.FC = () => {
  const { showBanner, acceptAll, acceptEssentialOnly, savePreferences } = useCookieConsent();
  const [visible, setVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [analyticsOn, setAnalyticsOn] = useState(false);

  // v1.3 - Suppress banner on /legal/* pages
  const location = useLocation();

  // Brief delay before slide-in so page loads first
  useEffect(() => {
    if (showBanner) {
      const t = setTimeout(() => setVisible(true), 400);
      return () => clearTimeout(t);
    } else {
      setVisible(false);
    }
  }, [showBanner]);

  // Suppress on /legal/* and when banner is not needed
  if (!showBanner || location.pathname.startsWith('/legal/')) return null;

  return (
    <Slide direction="up" in={visible} mountOnEnter unmountOnExit>
      <Box
        role="region"
        aria-label="Cookie consent"
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1400,
          boxShadow: '0 -2px 16px rgba(0,0,0,0.35)',
        }}
      >
        {/* Manage preferences panel — expands upward above main bar */}
        <Collapse in={showPreferences}>
          <Box
            sx={{
              bgcolor: '#111827',
              px: { xs: 2, sm: 4, md: 6 },
              py: 2,
              borderTop: '1px solid #374151',
            }}
          >
            {/* Essential cookies row */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                py: 1.5,
              }}
            >
              <Box>
                <Typography variant="body2" sx={{ color: '#f9fafb', fontWeight: 600 }}>
                  Essential cookies
                </Typography>
                <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                  Required for the site to work. Always active.
                </Typography>
              </Box>
              <Switch
                checked={true}
                disabled
                sx={{ '& .MuiSwitch-switchBase.Mui-disabled': { color: '#6b7280' } }}
              />
            </Box>
            <Divider sx={{ borderColor: '#374151' }} />
            {/* Analytics cookies row */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                py: 1.5,
              }}
            >
              <Box>
                <Typography variant="body2" sx={{ color: '#f9fafb', fontWeight: 600 }}>
                  Analytics cookies
                </Typography>
                <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                  Help us understand how visitors use the site.
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: '#6b7280', display: 'block', mt: 0.25, fontStyle: 'italic' }}
                >
                  No analytics tools are currently active.
                </Typography>
              </Box>
              <Switch
                checked={analyticsOn}
                onChange={(e) => setAnalyticsOn(e.target.checked)}
                sx={{ color: '#1d4ed8' }}
              />
            </Box>
            <Box sx={{ pt: 1.5 }}>
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  savePreferences(analyticsOn);
                  setShowPreferences(false);
                }}
                sx={{ bgcolor: '#1d4ed8', '&:hover': { bgcolor: '#1e40af' } }}
              >
                Save preferences
              </Button>
            </Box>
          </Box>
        </Collapse>

        {/* Main banner bar */}
        <Box
          sx={{
            bgcolor: '#1a1f2e',
            color: '#ffffff',
            px: { xs: 2, sm: 4, md: 6 },
            py: { xs: 2, sm: 2.5 },
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: { xs: 1.5, md: 3 },
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: '#cbd5e1', flexGrow: 1, lineHeight: 1.65 }}
          >
            We use cookies to improve your experience. Essential cookies are always
            active. You can accept optional analytics cookies or manage your
            preferences.{' '}
            <Link
              href="/legal/cookies"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: '#93c5fd', textDecorationColor: '#93c5fd' }}
            >
              Cookie Policy
            </Link>
          </Typography>

          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              flexShrink: 0,
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={acceptEssentialOnly}
              sx={{
                color: '#cbd5e1',
                borderColor: '#4b5563',
                fontSize: '0.8125rem',
                '&:hover': {
                  borderColor: '#9ca3af',
                  bgcolor: 'rgba(255,255,255,0.05)',
                },
                whiteSpace: 'nowrap',
              }}
            >
              Essential Only
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={() => setShowPreferences((p) => !p)}
              sx={{
                color: '#9ca3af',
                fontSize: '0.8125rem',
                whiteSpace: 'nowrap',
              }}
            >
              Manage preferences
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={acceptAll}
              sx={{
                bgcolor: '#1d4ed8',
                fontSize: '0.8125rem',
                '&:hover': { bgcolor: '#1e40af' },
                whiteSpace: 'nowrap',
              }}
            >
              Accept All
            </Button>
          </Box>
        </Box>
      </Box>
    </Slide>
  );
};

export default CookieConsentBanner;
